/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/check-param-names */
/**
 * ScatterChart Component
 *
 * A reusable scatter plot component built with Recharts.
 * Supports category-based coloring, quadrant backgrounds, and interactive tooltips.
 *
 * @example
 * // Basic scatter chart
 * <ScatterChart
 *   data={[
 *     { x: 4, y: 5, label: 'Point A' },
 *     { x: 8, y: 8.5, label: 'Point B' },
 *   ]}
 *   xKey="x"
 *   yKey="y"
 *   labelKey="label"
 * />
 *
 * @example
 * // ROI vs Effort with quadrants
 * <ScatterChart
 *   data={[
 *     { effort: 4, roi: 5, category: 'Quick Win', title: 'Pricing' },
 *     { effort: 8, roi: 8.5, category: 'Strategic', title: 'Digital Experience' },
 *   ]}
 *   xKey="effort"
 *   yKey="roi"
 *   labelKey="title"
 *   categoryKey="category"
 *   categoryColors={{
 *     'Quick Win': '#10B981',
 *     'Strategic': '#2563EB',
 *   }}
 *   xDomain={[0, 10]}
 *   yDomain={[0, 10]}
 *   quadrants={{
 *     topLeft: { label: 'Quick Win', color: '#10B98120' },
 *     topRight: { label: 'Strategic', color: '#2563EB20' },
 *   }}
 *   quadrantLines={{ x: 5, y: 5 }}
 * />
 */

import { useMemo } from 'react';

import PropTypes from 'prop-types';
import {
  ScatterChart as RechartsScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ZAxis,
  ReferenceLine,
  ReferenceArea
} from 'recharts';

import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import * as Styled from '../Charts.style';
import { getChartColors, getColorFromMap } from '../utils/colors';
import { identity } from '../utils/formatters';
/**
 * Custom tooltip component
 * @param root0
 * @param root0.active
 * @param root0.payload
 * @param root0.formatTooltip
 * @param root0.formatX
 * @param root0.formatY
 * @param root0.labelKey
 */
const CustomTooltip = ({ active, payload, formatTooltip, formatX, formatY, labelKey }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;

  if (!item) {
    return null;
  }

  // Use custom formatter if provided
  if (formatTooltip) {
    const tooltipData = formatTooltip(item);

    return (
      <Styled.TooltipContainer>
        {tooltipData.title && (
          <Styled.TooltipTitle size="xs">{tooltipData.title}</Styled.TooltipTitle>
        )}
        {Object.entries(tooltipData)
          .filter(([key]) => key !== 'title' && key !== 'badge')
          .map(([label, value]) => (
            <Styled.TooltipRow key={label}>
              <Text size="xs">{label}:</Text>
              <Text size="xs" weight="medium">
                {value}
              </Text>
            </Styled.TooltipRow>
          ))}
        {tooltipData.badge && (
          <Styled.TooltipBadge color={item.fill} size="xs">
            {tooltipData.badge}
          </Styled.TooltipBadge>
        )}
      </Styled.TooltipContainer>
    );
  }

  // Default tooltip
  const title = labelKey ? item[labelKey] : `Point (${formatX(item.x)}, ${formatY(item.y)})`;

  return (
    <Styled.TooltipContainer>
      <Styled.TooltipTitle size="xs">{title}</Styled.TooltipTitle>
      <Styled.TooltipRow>
        <Text size="xs">X:</Text>
        <Text size="xs" weight="medium">
          {formatX(item.x)}
        </Text>
      </Styled.TooltipRow>
      <Styled.TooltipRow>
        <Text size="xs">Y:</Text>
        <Text size="xs" weight="medium">
          {formatY(item.y)}
        </Text>
      </Styled.TooltipRow>
      {item.category && (
        <Styled.TooltipBadge color={item.fill} size="xs">
          {item.category}
        </Styled.TooltipBadge>
      )}
    </Styled.TooltipContainer>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  formatTooltip: PropTypes.func,
  formatX: PropTypes.func,
  formatY: PropTypes.func,
  labelKey: PropTypes.string
};

/**
 * Custom dot renderer
 * @param props
 * @param pointSize
 * @param categoryColors
 * @param color
 * @param defaultColor
 */
const renderDot = (props, pointSize, categoryColors, defaultColor) => {
  const { cx, cy, payload } = props;
  const fillColor = payload.category
    ? getColorFromMap(payload.category, categoryColors, defaultColor)
    : defaultColor;

  return (
    <circle
      cx={cx}
      cy={cy}
      fill={fillColor}
      r={pointSize}
      stroke="white"
      strokeWidth={2}
      style={{ cursor: 'pointer' }}
    />
  );
};

/**
 * ScatterChart Component
 * @param root0
 * @param root0.data
 * @param root0.xKey
 * @param root0.yKey
 * @param root0.labelKey
 * @param root0.categoryKey
 * @param root0.color
 * @param root0.categoryColors
 * @param root0.xDomain
 * @param root0.yDomain
 * @param root0.xAxisLabel
 * @param root0.yAxisLabel
 * @param root0.formatX
 * @param root0.formatY
 * @param root0.formatTooltip
 * @param root0.quadrants
 * @param root0.quadrantLines
 * @param root0.pointSize
 * @param root0.height
 * @param root0.showLegend
 * @param root0.showGrid
 * @param root0.title
 * @param root0.margin
 */
const ScatterChart = ({
  // Data
  data = [],
  xKey = 'x',
  yKey = 'y',
  labelKey = null, // eslint-disable-line no-unused-vars
  categoryKey = null,

  // Colors
  color = null,
  categoryColors = {},

  // Axis configuration
  xDomain = ['auto', 'auto'],
  yDomain = ['auto', 'auto'],
  xAxisLabel = '',
  yAxisLabel = '',
  formatX = identity,
  formatY = identity,

  // Tooltip
  formatTooltip = null, // eslint-disable-line no-unused-vars

  // Quadrants (for 2x2 matrix charts)
  quadrants = null,
  quadrantLines = { x: null, y: null },

  // Point styling
  pointSize = 10,

  // Layout
  height = 400,
  showLegend = true,
  showGrid = true,
  title = '',

  // Margins
  margin = { top: 20, right: 30, left: 20, bottom: 40 }
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const defaultColor = color || chartColors.primary;
  // Process data with category colors
  const chartData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        x: item[xKey],
        y: item[yKey],
        category: categoryKey ? item[categoryKey] : null,
        fill: categoryKey
          ? getColorFromMap(item[categoryKey], categoryColors, defaultColor)
          : defaultColor
      })),
    [data, xKey, yKey, categoryKey, categoryColors, defaultColor]
  );

  // Group data by category for separate scatter series
  const groupedData = useMemo(() => {
    if (!categoryKey) {
      return { _all: chartData };
    }

    const groups = {};
    chartData.forEach((item) => {
      const cat = item.category || '_other';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(item);
    });

    return groups;
  }, [chartData, categoryKey]);

  // Get unique categories for legend
  const categories = useMemo(
    () => Object.keys(groupedData).filter((k) => k !== '_all'),
    [groupedData]
  );

  // Empty state
  if (!data?.length) {
    return (
      <Styled.ChartWrapper>
        {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
        <Styled.EmptyState>No data available</Styled.EmptyState>
      </Styled.ChartWrapper>
    );
  }

  // Calculate quadrant boundaries
  const [xMin, xMax] =
    xDomain[0] === 'auto' || xDomain[1] === 'auto'
      ? [Math.min(...chartData.map((d) => d.x)), Math.max(...chartData.map((d) => d.x))]
      : xDomain;
  const [yMin, yMax] =
    yDomain[0] === 'auto' || yDomain[1] === 'auto'
      ? [Math.min(...chartData.map((d) => d.y)), Math.max(...chartData.map((d) => d.y))]
      : yDomain;

  const xMid = quadrantLines.x ?? (xMin + xMax) / 2;
  const yMid = quadrantLines.y ?? (yMin + yMax) / 2;

  // Custom legend renderer
  const renderLegend = () => {
    if (!showLegend || categories.length === 0) {
      return null;
    }

    return (
      <Styled.LegendContainer>
        {categories.map((cat) => (
          <Styled.LegendItem key={cat}>
            <Styled.LegendColor
              style={{
                backgroundColor: getColorFromMap(cat, categoryColors, defaultColor),
                borderRadius: '50%'
              }}
            />
            <Styled.LegendText>{cat}</Styled.LegendText>
          </Styled.LegendItem>
        ))}
      </Styled.LegendContainer>
    );
  };

  return (
    <Styled.ChartWrapper>
      {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
      <ResponsiveContainer height={height} width="100%">
        <RechartsScatterChart margin={margin}>
          {showGrid && <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />}

          {/* Quadrant backgrounds with labels */}
          {quadrants && (
            <>
              {quadrants.topLeft && (
                <ReferenceArea
                  fill={quadrants.topLeft.color}
                  fillOpacity={1}
                  label={
                    quadrants.topLeft.label
                      ? {
                          value: quadrants.topLeft.label,
                          position: 'insideTopLeft',
                          fontSize: 10,
                          fontWeight: 600,
                          fill: chartColors.axis,
                          opacity: 0.5,
                          offset: 10
                        }
                      : undefined
                  }
                  x1={xDomain[0] === 'auto' ? xMin : xDomain[0]}
                  x2={xMid}
                  y1={yMid}
                  y2={yDomain[1] === 'auto' ? yMax : yDomain[1]}
                />
              )}
              {quadrants.topRight && (
                <ReferenceArea
                  fill={quadrants.topRight.color}
                  fillOpacity={1}
                  label={
                    quadrants.topRight.label
                      ? {
                          value: quadrants.topRight.label,
                          position: 'insideTopRight',
                          fontSize: 10,
                          fontWeight: 600,
                          fill: chartColors.axis,
                          opacity: 0.5,
                          offset: 10
                        }
                      : undefined
                  }
                  x1={xMid}
                  x2={xDomain[1] === 'auto' ? xMax : xDomain[1]}
                  y1={yMid}
                  y2={yDomain[1] === 'auto' ? yMax : yDomain[1]}
                />
              )}
              {quadrants.bottomLeft && (
                <ReferenceArea
                  fill={quadrants.bottomLeft.color}
                  fillOpacity={1}
                  label={
                    quadrants.bottomLeft.label
                      ? {
                          value: quadrants.bottomLeft.label,
                          position: 'insideBottomLeft',
                          fontSize: 10,
                          fontWeight: 600,
                          fill: chartColors.axis,
                          opacity: 0.5,
                          offset: 10
                        }
                      : undefined
                  }
                  x1={xDomain[0] === 'auto' ? xMin : xDomain[0]}
                  x2={xMid}
                  y1={yDomain[0] === 'auto' ? yMin : yDomain[0]}
                  y2={yMid}
                />
              )}
              {quadrants.bottomRight && (
                <ReferenceArea
                  fill={quadrants.bottomRight.color}
                  fillOpacity={1}
                  label={
                    quadrants.bottomRight.label
                      ? {
                          value: quadrants.bottomRight.label,
                          position: 'insideBottomRight',
                          fontSize: 10,
                          fontWeight: 600,
                          fill: chartColors.axis,
                          opacity: 0.5,
                          offset: 10
                        }
                      : undefined
                  }
                  x1={xMid}
                  x2={xDomain[1] === 'auto' ? xMax : xDomain[1]}
                  y1={yDomain[0] === 'auto' ? yMin : yDomain[0]}
                  y2={yMid}
                />
              )}
            </>
          )}

          {/* Quadrant divider lines */}
          {quadrantLines.x != null && (
            <ReferenceLine stroke={chartColors.axis} strokeDasharray="5 5" x={quadrantLines.x} />
          )}
          {quadrantLines.y != null && (
            <ReferenceLine stroke={chartColors.axis} strokeDasharray="5 5" y={quadrantLines.y} />
          )}

          <XAxis
            dataKey="x"
            domain={xDomain}
            label={
              xAxisLabel
                ? { value: xAxisLabel, position: 'bottom', offset: 0, fontSize: 12 }
                : undefined
            }
            name="X"
            stroke={chartColors.axis}
            tick={{ fontSize: 12 }}
            tickFormatter={formatX}
            type="number"
          />

          <YAxis
            dataKey="y"
            domain={yDomain}
            label={
              yAxisLabel
                ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12 }
                : undefined
            }
            name="Y"
            stroke={chartColors.axis}
            tick={{ fontSize: 12 }}
            tickFormatter={formatY}
            type="number"
          />

          <ZAxis range={[pointSize * pointSize, pointSize * pointSize * 4]} />

          {/* <Tooltip
            content={
              <CustomTooltip
                formatTooltip={formatTooltip}
                formatX={formatX}
                formatY={formatY}
                labelKey={labelKey}
              />
            }
          /> */}

          {showLegend && categories.length > 0 && <Legend content={renderLegend} />}

          {/* Render scatter points */}
          {categoryKey ? (
            // Multiple scatters for each category
            Object.entries(groupedData).map(([cat, items]) => (
              <Scatter
                key={cat}
                data={items}
                fill={getColorFromMap(cat, categoryColors, defaultColor)}
                name={cat}
                shape={(props) => renderDot(props, pointSize, categoryColors, defaultColor)}
              />
            ))
          ) : (
            // Single scatter
            <Scatter
              data={chartData}
              fill={defaultColor}
              shape={(props) => renderDot(props, pointSize, categoryColors, defaultColor)}
            />
          )}
        </RechartsScatterChart>
      </ResponsiveContainer>
    </Styled.ChartWrapper>
  );
};

ScatterChart.propTypes = {
  /** Array of data objects */
  data: PropTypes.arrayOf(PropTypes.object),
  /** Key for X-axis values */
  xKey: PropTypes.string,
  /** Key for Y-axis values */
  yKey: PropTypes.string,
  /** Key for point labels/titles (optional) */
  labelKey: PropTypes.string,
  /** Key for category grouping/coloring (optional) */
  categoryKey: PropTypes.string,
  /** Default point color */
  color: PropTypes.string,
  /** Map of category values to colors */
  categoryColors: PropTypes.object,
  /** X-axis domain [min, max] or 'auto' */
  xDomain: PropTypes.array,
  /** Y-axis domain [min, max] or 'auto' */
  yDomain: PropTypes.array,
  /** X-axis label */
  xAxisLabel: PropTypes.string,
  /** Y-axis label */
  yAxisLabel: PropTypes.string,
  /** Formatter for X-axis values */
  formatX: PropTypes.func,
  /** Formatter for Y-axis values */
  formatY: PropTypes.func,
  /** Custom tooltip formatter: (dataPoint) => { title, ...labels, badge } */
  formatTooltip: PropTypes.func,
  /** Quadrant configuration: { topLeft, topRight, bottomLeft, bottomRight } */
  quadrants: PropTypes.shape({
    topLeft: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
    topRight: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
    bottomLeft: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
    bottomRight: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string })
  }),
  /** Quadrant divider line positions */
  quadrantLines: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  /** Point radius in pixels */
  pointSize: PropTypes.number,
  /** Chart height in pixels */
  height: PropTypes.number,
  /** Show category legend */
  showLegend: PropTypes.bool,
  /** Show grid lines */
  showGrid: PropTypes.bool,
  /** Chart title */
  title: PropTypes.string,
  /** Chart margins */
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  })
};

export default ScatterChart;
